import type {
  AuthResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  RegisterResponse,
  AcceptInviteRequest,
  Attempt,
  AttemptMode,
  Chapter,
  Project,
  ProjectVersion,
  StepEvent,
  StepWithRegions,
  PublicShareInfo,
} from '@simultrain/shared';
import { useAuth } from '../auth/store.js';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

class ApiError extends Error {
  constructor(public status: number, public data: unknown) {
    super(`API ${status}`);
  }
}

async function request<T>(path: string, init: RequestInit = {}, isRetry = false): Promise<T> {
  const { accessToken, refreshToken, setTokens, logout } = useAuth.getState();
  const headers = new Headers(init.headers);
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(`${API_URL}${path}`, { ...init, headers });
  if (res.status === 401 && !isRetry && refreshToken) {
    const refreshed = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (refreshed.ok) {
      const tokens = await refreshed.json();
      setTokens(tokens.accessToken, tokens.refreshToken);
      return request<T>(path, init, true);
    }
    logout();
    throw new ApiError(401, null);
  }

  if (!res.ok) {
    let data: unknown = null;
    try {
      data = await res.json();
    } catch {}
    throw new ApiError(res.status, data);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  login: (email: string, password: string) =>
    request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  register: (email: string, password: string, displayName: string, locale: 'nl' | 'en' = 'nl') =>
    request<RegisterResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, displayName, locale }),
    }),
  acceptInvite: (token: string, body: AcceptInviteRequest) =>
    request<AuthResponse>(`/invites/${token}/accept`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  changePassword: (body: ChangePasswordRequest) =>
    request<ChangePasswordResponse>('/auth/change-password', { method: 'POST', body: JSON.stringify(body) }),
  listProjects: () => request<Project[]>('/projects'),
  getProject: (id: string) =>
    request<Project & { versions: ProjectVersion[] }>(`/projects/${id}`),
  listChapters: (versionId: string) => request<Chapter[]>(`/versions/${versionId}/chapters`),
  getChapterFull: (id: string) =>
    request<{ chapter: Chapter; steps: StepWithRegions[]; ttsAudio: Record<string, string> }>(
      `/chapters/${id}/full`,
    ),

  startAttempt: (chapterId: string, mode: AttemptMode) =>
    request<Attempt>('/attempts', {
      method: 'POST',
      body: JSON.stringify({ chapterId, mode }),
    }),
  finishAttempt: (
    id: string,
    status: 'passed' | 'failed' | 'abandoned',
    events: StepEvent[],
  ) =>
    request<Attempt>(`/attempts/${id}/finish`, {
      method: 'POST',
      body: JSON.stringify({ status, events }),
    }),

  assetUrl: (id: string) => `${API_URL}/assets/${id}`,

  getPublicShareInfo: (token: string) => request<PublicShareInfo>(`/share/${token}`),
  getSharedChapterFull: (token: string) =>
    request<{ chapter: Chapter; steps: StepWithRegions[]; ttsAudio: Record<string, string> }>(
      `/share/${token}/full`,
    ),
  sharedAssetUrl: (token: string, assetId: string) => `${API_URL}/share/${token}/assets/${assetId}`,
};

export { ApiError };
